import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import ImageCropModal from '../../../common/image-crop-rotate/ImageCropModal';
import { imageUtils } from '../../../../utils/image.utils';

const UploadSection = ({
  uploadType,
  setUploadType,
  setImageName,
  setImage,
  setShowImageError,
  video,
  setVideo,
  handleYoutube,
  setYoutubeUrl,
  picturePreview,
  setPicturePreview,
  videoPreview,
  setVideoPreview,
  youtubeUrl,
  setImageError,
  setNoVideoError,
}) => {
  const { handleSubmit, register } = useForm();
  const [picture, setPicture] = useState(null);
  const [videoError, setVideoError] = useState(null);
  const fileInputRef = useRef(null);
  const [imageCropPopup, setImageCropPopup] = useState(false);
  const [imageForCrop, setImageForCrop] = useState(null);

  const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100 MB

  const handleUploadTypeChange = (newUploadType) => {
    if (newUploadType) {
      setUploadType(newUploadType);
      // setPicture(null);
      // setPicturePreview(null);
      // setVideo(null);
      // if (newUploadType === 'picture') setVideoPreview(null);
      setImageError(null);
      setNoVideoError(null);
      setVideoError(null); // Clear any previous errors
    }
  };

  const handleImageChange = (e) => {
    const files = e.target.files;
    if (files.length) {
      setImageForCrop(files[0]);
      setImageCropPopup(true);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_VIDEO_SIZE) {
        setVideoError('Video file size should not exceed 100 MB');
        setVideo(null);
        setVideoPreview(null);
      } else {
        setVideo(file);
        setVideoError(null); // Clear error if valid
        const previewUrl = URL.createObjectURL(file);
        setVideoPreview(previewUrl);
        // setYoutubeUrl(null);
      }
    }
  };

  const removePreview = () => {
    if (uploadType === 'picture') {
      setPicture(null);
      setPicturePreview(null);
      setImageName(null);
      setImage(null);
      setImageCropPopup(false);
      setImageForCrop(null);
    } else {
      setVideo(null);
      setVideoPreview(null);
      setVideoError(null); // Clear error when removing
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // Reset the file input
    }
  };

  const onCropComplete = (croppedImage) => {
    const imagePromise = imageUtils.getImagePromise(croppedImage);
    imagePromise.then((img) => {
      setImageName(img.name);
      setImage(img);
      setImageCropPopup(false);
      setImageForCrop(null);
      setShowImageError(false);
      setPicture(img);
      const previewUrl = URL.createObjectURL(img);
      setPicturePreview(previewUrl);
      // setYoutubeUrl(null);
    });
  };

  return (
    <Container
      disableGutters
      maxWidth="sm"
      sx={{ paddingLeft: 0, paddingRight: 0 }}
    >
      {imageCropPopup && imageForCrop && (
        <ImageCropModal
          imageCropPopup={imageCropPopup}
          setImageCropPopup={setImageCropPopup}
          image={imageForCrop}
          setPicture={onCropComplete}
          heading="Edit your challenge image"
          aspectRatio={16 / 9}
        />
      )}
      <Typography
        variant="body1"
        sx={{ mb: 1, fontWeight: 600, fontFamily: 'Open Sans, serif' }}
      >
        Choose Upload Type
      </Typography>

      <Box display="flex" gap={2} mb={3}>
        <Button
          variant="contained"
          sx={{
            px: 4,
            color: uploadType === 'picture' ? '#fff' : '#321B6D',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '25px',
            background:
              uploadType === 'picture'
                ? 'linear-gradient(90deg, #6DBCE8 0%, #C844F3 48%, #361A71 100%)'
                : '#fff',
            '&:hover': { backgroundColor: '#fff' },
          }}
          onClick={() => handleUploadTypeChange('picture')}
        >
          Picture
        </Button>

        <Button
          variant="contained"
          sx={{
            px: 4,
            color: uploadType === 'video' ? '#fff' : '#321B6D',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '25px',
            background:
              uploadType === 'video'
                ? 'linear-gradient(90deg, #6DBCE8 0%, #C844F3 48%, #361A71 100%)'
                : '#fff',
            '&:hover': { backgroundColor: '#fff' },
          }}
          onClick={() => handleUploadTypeChange('video')}
        >
          Video
        </Button>
      </Box>

      <Box
        sx={{
          border: '1px dashed #cccccc',
          p: 3,
          textAlign: 'center',
          borderRadius: 2,
          mb: 3,
        }}
      >
        <input
          type="file"
          accept={uploadType === 'picture' ? 'image/*' : 'video/*'}
          style={{ display: 'none' }}
          id="file-upload"
          ref={fileInputRef}
          onChange={
            uploadType === 'picture' ? handleImageChange : handleVideoChange
          }
        />
        <label htmlFor="file-upload">
          <Button
            variant="contained"
            sx={{
              mb: 2,
              px: 4,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '25px',
              background:
                'linear-gradient(90deg, #6DBCE8 0%, #C844F3 48%, #361A71 100%)',
            }}
            component="span"
          >
            Upload {uploadType === 'video' ? 'video' : 'image'}
          </Button>
        </label>

        <Typography variant="body2" sx={{ color: '#888888', mb: 2 }}>
          {uploadType === 'picture'
            ? 'Drag and drop or browse to choose an image file'
            : 'Upload a video or add a YouTube URL (Max: 100 MB)'}
        </Typography>

        {videoError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {videoError}
          </Typography>
        )}

        {uploadType === 'video' && (
          <TextField
            placeholder="Enter YouTube URL"
            variant="outlined"
            fullWidth
            sx={{
              mt: 1,
              background: '#f9f9f9',
              borderRadius: '5px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#cccccc',
                },
                '&:hover fieldset': {
                  borderColor: '#888888',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#cccccc',
                },
              },
            }}
            {...register('youtubeUrl')}
            onChange={handleYoutube}
            value={youtubeUrl}
          />
        )}

        {(picturePreview && uploadType === 'picture') ||
        (videoPreview && uploadType === 'video') ? (
          <Box sx={{ position: 'relative', mt: 2 }}>
            <IconButton
              onClick={removePreview}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                zIndex: 9,
              }}
            >
              <CloseIcon />
            </IconButton>

            {picturePreview && uploadType === 'picture' && (
              <img
                src={picturePreview}
                alt="Preview"
                width="100%"
                style={{ borderRadius: '5px' }}
              />
            )}

            {videoPreview && uploadType === 'video' && (
              <video
                src={videoPreview}
                controls
                width="100%"
                style={{ borderRadius: '5px' }}
              />
            )}
          </Box>
        ) : null}
      </Box>
    </Container>
  );
};

export default UploadSection;
